import { createMutation, createQuery } from 'react-query-kit';

import { fetchData } from 'services/http/http';
import { getMessageOrEnvelope } from 'services/repositories/helpers/helpers';
import { TransactionInternalType } from 'support/types';
import portalSchema from 'support/types/schema-portal';

export type GetMessageResponse =
  portalSchema.paths['/v1/messages/{messageId}']['get']['responses']['200']['content']['application/json'];

export const useMessage = createQuery<GetMessageResponse, { messageId: string | undefined }, Error>({
  primaryKey: '/messages/:messageId',
  queryFn: async ({ queryKey: [, { messageId }] }) => {
    const { data } = await fetchData<GetMessageResponse>(`/v1/messages/${messageId}`);
    return data;
  },
  enabled: (_, { messageId }) => !!messageId,
});

export type GetMessageWithProcessSpecificationResponse =
  portalSchema.paths['/v2/messages/{messageId}']['get']['responses']['200']['content']['application/json'];
export const useMessageWithProcessSpecification = createQuery<
  GetMessageWithProcessSpecificationResponse,
  { messageId: string | undefined },
  Error
>({
  primaryKey: '/messages/v2/:messageId',
  queryFn: async ({ queryKey: [, { messageId }] }) => {
    const { data } = await fetchData<GetMessageWithProcessSpecificationResponse>(`/v2/messages/${messageId}`);
    return data;
  },
  enabled: (_, { messageId }) => !!messageId,
});

export type UpdateMessageRequest =
  portalSchema.paths['/v1/messages/{messageId}']['put']['requestBody']['content']['application/json'];
export type UpdateMessageResponse =
  portalSchema.paths['/v1/messages/{messageId}']['put']['responses']['200']['content']['application/json'];
export const useUpdateMessage = createMutation<
  UpdateMessageResponse,
  { messageId: string; data: UpdateMessageRequest },
  Error
>({
  mutationFn: async ({ messageId, data }) => {
    const { data: responseData } = await fetchData<UpdateMessageResponse>(`/v1/messages/${messageId}`, {
      method: 'put',
      data,
    });
    return responseData;
  },
});

export type ReprocessMessageResponse =
  portalSchema.paths['/v2/messages/{messageId}/resend']['post']['responses']['202']['content']['application/json'];

export type ReprocessMessageResult = {
  transactionId: string;
  transactionType: TransactionInternalType;
};

export const useReprocessMessage = createMutation<
  ReprocessMessageResult,
  { messageId: string; waitForMessage?: boolean },
  Error
>({
  mutationFn: async ({ messageId, waitForMessage = true }) => {
    const { data: responseData } = await fetchData<ReprocessMessageResponse>(`/v2/messages/${messageId}/resend`, {
      method: 'post',
    });

    const { transactionId, transactionType } = await getMessageOrEnvelope(responseData.data.id, waitForMessage);

    return { transactionId, transactionType };
  },
});

export type ResendMessageToReceiverResponse =
  portalSchema.paths['/v2/messages/{messageId}/resend-as-receiver']['post']['responses']['202']['content']['application/json'];

export const useResendMessageToReceiver = createMutation<
  ResendMessageToReceiverResponse['data'],
  { messageId: string; reason: string | null },
  Error
>({
  mutationFn: async ({ messageId, reason }) => {
    const { data: responseData } = await fetchData<ResendMessageToReceiverResponse>(
      `/v2/messages/${messageId}/resend-as-receiver`,
      {
        method: 'post',
        data: { reason },
      },
    );

    return responseData.data;
  },
});
